// Tracks recent route visits in memory so feedback submissions can capture
// not just "where the user is now" but "what they did just before this" —
// a sense of sequence, not a single snapshot. Plain module-level state (same
// pattern as chatUtils.ts's global drawer hooks) rather than React context,
// since nothing needs to re-render off it — it's only ever read once, at the
// moment a feedback submission is captured.

export interface NavigationHistoryEntry {
  route: string;
  label: string;
  visitedAt: number;
}

const MAX_ENTRIES = 5;
let history: NavigationHistoryEntry[] = [];

// Called on every route change (see NavigationHistoryTracker.tsx). Skips a
// route that's identical to the last one recorded, so query-param-only
// updates on the same page (e.g. tab switches) don't spam the trail with
// near-duplicate entries.
export const recordNavigation = (entry: NavigationHistoryEntry) => {
  const last = history[history.length - 1];
  if (last && last.route === entry.route) return;

  history.push(entry);
  if (history.length > MAX_ENTRIES) {
    history = history.slice(history.length - MAX_ENTRIES);
  }
};

// The entry immediately before the current page — "what they did just
// before submitting." Returns null if there's no prior visit this session
// (e.g. feedback given on the very first page load).
export const getPreviousNavigation = (): NavigationHistoryEntry | null => {
  if (history.length < 2) return null;
  return history[history.length - 2];
};

// Full trail (oldest first), most recent last, capped at MAX_ENTRIES.
export const getNavigationTrail = (): NavigationHistoryEntry[] => [...history];
